// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Decimal from 'decimal.js';
import { inspect } from './inspect';
import { CONVERTIBLE_WEIGHT_UNITS } from './types';

const nowSeconds = (): number => Math.round(Date.now() / 1000);

const nowMilliseconds = (): number => Date.now();

const msToSeconds = (ms: number): number => Math.round(ms / 1000);

const round = (value: number, decimals = 2): number => {
  const multiplier = 10 ** decimals;

  return new Decimal(value).mul(multiplier).round().div(multiplier).toNumber();
};

const roundDecimal = (value: Decimal, decimals = 2): Decimal => {
  const multiplier = 10 ** decimals;

  return value.mul(multiplier).round().div(multiplier);
};

const getRandomInt = (min = 0, max = 10000): number => {
  const minCeiled = Math.ceil(min);
  const maxFloored = Math.floor(max);

  return Math.floor(Math.random() * (maxFloored - minCeiled + 1)) + minCeiled;
};

const todayStart = (timezoneIn?: string) => {
  if (timezoneIn) {
    dayjs.extend(utc);
    dayjs.extend(timezone);

    return dayjs().tz(timezoneIn).startOf('day');
  }

  return dayjs().startOf('day');
};

const toNoonTime = (timeIn: dayjs.Dayjs, timezoneIn: string): dayjs.Dayjs => {
  return timeIn.hour(12).minute(0).second(0).millisecond(0).tz(timezoneIn, true);
};

const todayEnd = (timezoneIn?: string) => {
  if (timezoneIn) {
    dayjs.extend(utc);
    dayjs.extend(timezone);

    return dayjs().tz(timezoneIn).endOf('day');
  }

  return dayjs().endOf('day');
};

function nullthrows<T>(value: T | null | undefined): T {
  if (value == null) {
    throw new Error('Unexpected null');
  }

  return value;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function assertNever(_value: never): void {
  // do nothing
}

function asErrorInstance(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  return new Error(`Unexpected error value: ${inspect(error)}`);
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
function isPromise(value: any): value is Promise<unknown> {
  return value != null && value.then != null && typeof value.then === 'function';
}

function notNull<T>(value: T | null | undefined): value is T {
  return value != null;
}

// Valid units: grams, ounces, pounds, kg
const convert = ({
  quantity,
  inputUnit,
  outputUnit,
}: {
  readonly quantity: number;
  readonly inputUnit: string;
  readonly outputUnit: string;
}): number => {
  // Ounces to... grams, pounds, kg
  if (inputUnit === CONVERTIBLE_WEIGHT_UNITS.OUNCES) {
    if (outputUnit === CONVERTIBLE_WEIGHT_UNITS.GRAMS) {
      return new Decimal(quantity).mul(28.34952).toNumber();
    }
    if (outputUnit === CONVERTIBLE_WEIGHT_UNITS.POUNDS) {
      return new Decimal(quantity).div(16).toNumber();
    }
    if (outputUnit === CONVERTIBLE_WEIGHT_UNITS.KG) {
      return new Decimal(quantity).div(35.274).toNumber();
    }
  }

  // Grams to... ounces, pounds, kg
  if (inputUnit === CONVERTIBLE_WEIGHT_UNITS.GRAMS) {
    if (outputUnit === CONVERTIBLE_WEIGHT_UNITS.OUNCES) {
      return new Decimal(quantity).div(28.34952).toNumber();
    }
    if (outputUnit === CONVERTIBLE_WEIGHT_UNITS.POUNDS) {
      return new Decimal(quantity).div(453.59237).toNumber();
    }
    if (outputUnit === CONVERTIBLE_WEIGHT_UNITS.KG) {
      return new Decimal(quantity).div(1000).toNumber();
    }
  }

  // Pounds to... grams, ounces, kg
  if (inputUnit === CONVERTIBLE_WEIGHT_UNITS.POUNDS) {
    if (outputUnit === CONVERTIBLE_WEIGHT_UNITS.GRAMS) {
      return new Decimal(quantity).mul(453.59237).toNumber();
    }
    if (outputUnit === CONVERTIBLE_WEIGHT_UNITS.OUNCES) {
      return new Decimal(quantity).mul(16).toNumber();
    }
    if (outputUnit === CONVERTIBLE_WEIGHT_UNITS.KG) {
      return new Decimal(quantity).div(2.20462262185).toNumber();
    }
  }

  // KG to... grams, ounces, pounds
  if (inputUnit === CONVERTIBLE_WEIGHT_UNITS.KG) {
    if (outputUnit === CONVERTIBLE_WEIGHT_UNITS.GRAMS) {
      return new Decimal(quantity).mul(1000).toNumber();
    }
    if (outputUnit === CONVERTIBLE_WEIGHT_UNITS.OUNCES) {
      return new Decimal(quantity).mul(35.274).toNumber();
    }
    if (outputUnit === CONVERTIBLE_WEIGHT_UNITS.POUNDS) {
      return new Decimal(quantity).mul(2.20462262185).toNumber();
    }
  }

  return quantity;
};

export interface ErrorWithCode extends Error {
  code?: string;
  status?: number;
}

const formatDuration = (timeSeconds: number) => {
  if (timeSeconds === 0) {
    return '0 days';
  }
  const days = Math.floor(timeSeconds / (3600 * 24));
  const hours = Math.floor((timeSeconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((timeSeconds % 3600) / 60);
  const seconds = Math.floor(timeSeconds % 60);

  const displayD = days > 0;
  const displayH = hours > 0;
  const displayM = minutes > 0;
  const displayS = seconds > 0;

  const dDisplay = displayD ? String(days) + (days === 1 ? ' day' : ' days') : '';
  const hDisplay = displayH ? String(hours) + (hours === 1 ? ' hr' : ' hrs') : '';
  const mDisplay = displayM ? String(minutes) + (minutes === 1 ? ' min' : ' mins') : '';
  const sDisplay = displayS ? String(seconds) + (seconds === 1 ? ' second' : ' seconds') : '';

  const dComma = displayD && (displayH || displayM || displayS) ? ', ' : '';
  const hComma = displayH && (displayM || displayS) ? ', ' : '';
  const mComma = displayM && displayS ? ', ' : '';

  return `${dDisplay}${dComma}${hDisplay}${hComma}${mDisplay}${mComma}${sDisplay}`;
};

function moveDecimalRightAndFloor(num: number, places = 2): number {
  const decimal = new Decimal(num);
  const multiplier = 10 ** places;

  return decimal.mul(multiplier).floor().toNumber();
}

function moveDecimalLeft(num: number, places = 2): number {
  const decimal = new Decimal(num);
  const multiplier = 10 ** places;

  return decimal.div(multiplier).toNumber();
}

const addTraysRaw = (traysRaw: number, traysRawToAdd: number): number => {
  const integerTraysRaw = moveDecimalRightAndFloor(traysRaw);
  const integerTraysRawToAdd = moveDecimalRightAndFloor(traysRawToAdd);

  return moveDecimalLeft(integerTraysRaw + integerTraysRawToAdd);
};

const subtractTraysRaw = (traysRaw: number, traysRawToSubtract: number): number => {
  const integerTraysRaw = moveDecimalRightAndFloor(traysRaw);
  const integerTraysRawToSubtract = moveDecimalRightAndFloor(traysRawToSubtract);

  return moveDecimalLeft(integerTraysRaw - integerTraysRawToSubtract);
};

const multiplyTraysRaw = (traysRawA: number, multiplierB: number): number => {
  const integerTraysRawA = moveDecimalRightAndFloor(traysRawA);
  const integerMultiplierB = moveDecimalRightAndFloor(multiplierB);

  return round(moveDecimalLeft(integerTraysRawA * integerMultiplierB, 4), 2);
};

const ucFirst = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);
const lcFirst = (str: string): string => str.charAt(0).toLowerCase() + str.slice(1);

export const utils = {
  ucFirst,
  lcFirst,
  round,
  roundDecimal,
  nowSeconds,
  nowMilliseconds,
  msToSeconds,
  nullthrows,
  assertNever,
  notNull,
  isPromise,
  asErrorInstance,
  todayStart,
  todayEnd,
  toNoonTime,
  convert,
  formatDuration,
  addTraysRaw,
  subtractTraysRaw,
  multiplyTraysRaw,
  getRandomInt,
  emailRegex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
};
